import React, { useEffect, useMemo } from 'react';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from 'dayjs';
import { useSearchParams, useNavigate } from 'react-router-dom';
import {
    Box,
    CircularProgress,
    Paper,
    Typography,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Accordion,
    AccordionSummary,
    AccordionDetails,
    Grid,
    TextField,
    Button,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    List,
    ListItem,
    ListItemText,
    Chip,
    Divider,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow
} from '@mui/material';
import {
    ExpandMore as ExpandMoreIcon,
    Search as SearchIcon,
    CheckCircle as SuccessIcon,
    Error as ErrorIcon,
    Info as InfoIcon,
    Send as SendIcon,
    Message as MessageIcon
} from '@mui/icons-material';
import { useChemoRecords } from '../hooks/chemo/useChemoRecords';
import { useContext } from 'react';
import { RolesContext } from '../context/User';
import ChemoReportsList from '../component/ChemoRecords/ChemoReportsList';
import ChemoReportForm from '../component/ChemoRecords/ChemoReportForm';
import ChemoRecordsHeader from '../component/ChemoRecords/ChemoRecordsHeader';
import CustomSnackbar from '../component/CustomSnackbar';
import { styles, datePickerPopupSlotProps } from '../styles/ChemoRecordsStyles';
import { chemoTranslations } from '../hooks/chemo/chemoTranslations';
import { FIELD_NAMES, SERVER_VALUE_YES, DASH_PLACEHOLDER, CHECK_MARK, TOP_RECORDS_OPTIONS, EMPTY_STRING } from '../hooks/chemo/chemoConstants';

const ChemoRecordsPage = () => {
    const [stateroles] = useContext(RolesContext);
    const user = {
        hospital: stateroles?.siteLabel || null,
        roles: stateroles?.roles || [],
        name: stateroles?.name || null,
    };
    const [searchParams, setSearchParams] = useSearchParams();
    const navigate = useNavigate();

    const {
        loading,
        searchTerm,
        showForm,
        currentReport,
        filteredReports,
        fetchedRecords,
        isLoadingRecords,
        error,
        topRecords,
        snackbar,
        closeSnackbar,
        resultDialog,
        closeResultDialog,
        dateSearch,
        updateDateSearch,
        handleSubmit,
        handleSaveReport,
        handleTopRecordsChange,
        handleDateSearch,
        fetchHospitalReport,
        handleCheckboxChange,
        handleFieldChange,
        formatDate,
        openNewReportForm,
        loadPreviousReport,
        openExistingReport,
        closeForm,
        handleSearchChange,
        messageDialog,
        openMessageDialog,
        closeMessageDialog,
        updateMessageText,
        handleSendMessage
    } = useChemoRecords();

    const canCreate = user?.roles?.includes('hospital') || user?.roles?.includes('admin');
    const canView = user?.roles?.includes('hospital') || user?.roles?.includes('admin');

    const accordionList = useMemo(() => (fetchedRecords || []).map((report, reportIndex) => (
        <Accordion
            key={reportIndex}
            sx={{
                marginBottom: 2,
                border: '1px solid #e8edf2',
                borderRadius: '10px !important',
                boxShadow: '0 2px 8px rgba(0,0,0,0.04)',
                '&:before': { display: 'none' },
                '&.Mui-expanded': { boxShadow: '0 4px 16px rgba(52,152,219,0.12)', borderColor: '#3498db' }
            }}
        >
            <AccordionSummary
                expandIcon={<ExpandMoreIcon sx={{ color: '#3498db' }} />}
                sx={{
                    backgroundColor: '#f8fafb',
                    borderRadius: '10px',
                    borderLeft: '4px solid #3498db',
                    '&:hover': { backgroundColor: '#e3f2fd' },
                    '&.Mui-expanded': { borderRadius: '10px 10px 0 0', backgroundColor: '#e3f2fd' }
                }}
            >
                <Box sx={{ width: '100%' }}>
                    <Grid container spacing={2} alignItems="center">
                        <Grid item xs={12} sm={6} md={3}>
                            <Typography variant="caption" sx={{ color: '#64748b', display: 'block' }}>{chemoTranslations.form.hospital}</Typography>
                            <Typography variant="body2" sx={{ fontWeight: 700, color: '#1e293b' }}>{report[FIELD_NAMES.SPECVALUE] || DASH_PLACEHOLDER}</Typography>
                        </Grid>
                        <Grid item xs={6} sm={3} md={2}>
                            <Typography variant="caption" sx={{ color: '#64748b', display: 'block' }}>{chemoTranslations.form.startDate}</Typography>
                            <Typography variant="body2" sx={{ fontWeight: 500, color: '#334155' }}>{report[FIELD_NAMES.FROMDATE] ? formatDate(report[FIELD_NAMES.FROMDATE]) : DASH_PLACEHOLDER}</Typography>
                        </Grid>
                        <Grid item xs={6} sm={3} md={2}>
                            <Typography variant="caption" sx={{ color: '#64748b', display: 'block' }}>{chemoTranslations.form.endDate}</Typography>
                            <Typography variant="body2" sx={{ fontWeight: 500, color: '#334155' }}>{report[FIELD_NAMES.TODATE] ? formatDate(report[FIELD_NAMES.TODATE]) : DASH_PLACEHOLDER}</Typography>
                        </Grid>
                        <Grid item xs={6} sm={3} md={2}>
                            <Typography variant="caption" sx={{ color: '#64748b', display: 'block' }}>{chemoTranslations.labels.createdBy}</Typography>
                            <Typography variant="body2" sx={{ color: '#334155' }}>{report[FIELD_NAMES.SNAME] || DASH_PLACEHOLDER}</Typography>
                        </Grid>
                        <Grid item xs={6} sm={3} md={2}>
                            <Chip
                                label={`${report[FIELD_NAMES.SUBFORM]?.length || 0} ${chemoTranslations.labels.records}`}
                                size="small"
                                sx={{ backgroundColor: '#3498db', color: '#fff', fontWeight: 700, fontSize: '0.8rem' }}
                            />
                        </Grid>
                    </Grid>
                </Box>
            </AccordionSummary>
            <AccordionDetails sx={{ padding: { xs: 1.5, sm: 2 }, backgroundColor: '#fff', borderRadius: '0 0 10px 10px' }}>
                {/* Desktop Table */}
                <Box sx={{ overflow: 'auto', display: { xs: 'none', md: 'block' } }}>
                    <TableContainer sx={{ borderRadius: '8px', border: '1px solid #e8edf2' }} dir="rtl">
                        <Table size="small" sx={{ minWidth: 1100 }}>
                            <TableHead>
                                <TableRow sx={{ backgroundColor: '#f8fafb' }}>
                                    {[
                                        chemoTranslations.table.row,
                                        chemoTranslations.table.deliveryDate,
                                        chemoTranslations.table.customerName,
                                        chemoTranslations.table.customerNumber,
                                        chemoTranslations.table.day,
                                        chemoTranslations.table.description,
                                        chemoTranslations.table.willArrive,
                                        chemoTranslations.table.willNotArrive,
                                        chemoTranslations.table.endTreatment,
                                        chemoTranslations.table.updateDeliveryDate,
                                        chemoTranslations.table.remainingDoses,
                                        chemoTranslations.table.loaded
                                    ].map(h => (
                                        <TableCell key={h} align="center" sx={styles.tableHeaderCell}>{h}</TableCell>
                                    ))}
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {(report[FIELD_NAMES.SUBFORM] || []).map((record, index) => (
                                    <TableRow
                                        key={record[FIELD_NAMES.ORDI1] || index}
                                        sx={{
                                            '&:nth-of-type(odd)': { backgroundColor: '#fafbfc' },
                                            '&:nth-of-type(even)': { backgroundColor: '#fff' },
                                            '&:hover': { backgroundColor: '#e3f2fd' }
                                        }}
                                    >
                                        <TableCell align="center" sx={styles.tableCell}>{record[FIELD_NAMES.ROW_ID]}</TableCell>
                                        <TableCell align="center" sx={styles.tableCell}>{formatDate(record[FIELD_NAMES.DUEDATE])}</TableCell>
                                        <TableCell align="right" sx={{ ...styles.tableCell, direction: 'rtl' }}>{record[FIELD_NAMES.CDES]}</TableCell>
                                        <TableCell align="center" sx={{ ...styles.tableCell, fontWeight: 600, color: '#5e72e4' }}>{record[FIELD_NAMES.CUSTNAME]}</TableCell>
                                        <TableCell align="center" sx={styles.tableCell}>{record[FIELD_NAMES.DAY]}</TableCell>
                                        <TableCell align="right" sx={{ ...styles.tableCell, maxWidth: 200, direction: 'rtl' }}>{record[FIELD_NAMES.CUSTDES]}</TableCell>
                                        {[
                                            [FIELD_NAMES.APPROVED, '#e8f5e9', '#388e3c'],
                                            [FIELD_NAMES.NOT_APPROVED, '#ffebee', '#d32f2f'],
                                            [FIELD_NAMES.FINISH, '#e8eaf6', '#3949ab']
                                        ].map(([field, bg, color]) => (
                                            <TableCell key={field} align="center" sx={{ padding: '6px 4px' }}>
                                                {record[field] === SERVER_VALUE_YES ? (
                                                    <Chip size="small" label={CHECK_MARK} sx={{ backgroundColor: bg, color, fontWeight: 700, height: 22, fontSize: '0.75rem' }} />
                                                ) : (
                                                    <Typography sx={{ color: '#cbd5e0', fontSize: '0.85rem' }}>—</Typography>
                                                )}
                                            </TableCell>
                                        ))}
                                        <TableCell align="center" sx={styles.tableCell}>{record[FIELD_NAMES.TO_DUE_DATE] ? formatDate(record[FIELD_NAMES.TO_DUE_DATE]) : DASH_PLACEHOLDER}</TableCell>
                                        <TableCell align="center" sx={{ ...styles.tableCell, fontWeight: 700, color: '#5e72e4' }}>{record[FIELD_NAMES.ORDBAL]}</TableCell>
                                        <TableCell align="center" sx={{ padding: '6px 4px' }}>
                                            {record[FIELD_NAMES.LOADED] === SERVER_VALUE_YES ? (
                                                <Chip size="small" label={CHECK_MARK} sx={{ backgroundColor: '#e8f5e9', color: '#388e3c', fontWeight: 700, height: 22, fontSize: '0.75rem' }} />
                                            ) : (
                                                <Typography sx={{ color: '#cbd5e0', fontSize: '0.85rem' }}>—</Typography>
                                            )}
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </Box>
                {/* Mobile Cards */}
                <Box sx={{ display: { xs: 'block', md: 'none' } }}>
                    {(report[FIELD_NAMES.SUBFORM] || []).map((record, index) => (
                        <Paper key={index} sx={{ padding: 2, marginBottom: 1.5, border: '1px solid #e8edf2', borderRadius: '8px', boxShadow: '0 1px 4px rgba(0,0,0,0.04)' }}>
                            <Grid container spacing={1.5}>
                                <Grid item xs={6}><Typography variant="caption" sx={{ color: '#64748b', display: 'block' }}>{chemoTranslations.table.row}</Typography><Typography variant="body2" sx={{ fontWeight: 600, color: '#5e72e4' }}>{record[FIELD_NAMES.ROW_ID]}</Typography></Grid>
                                <Grid item xs={6}><Typography variant="caption" sx={{ color: '#64748b', display: 'block' }}>{chemoTranslations.table.deliveryDate}</Typography><Typography variant="body2">{formatDate(record[FIELD_NAMES.DUEDATE])}</Typography></Grid>
                                <Grid item xs={6}><Typography variant="caption" sx={{ color: '#64748b', display: 'block' }}>{chemoTranslations.table.customerNumber}</Typography><Typography variant="body2" sx={{ fontWeight: 600 }}>{record[FIELD_NAMES.CUSTNAME]}</Typography></Grid>
                                <Grid item xs={12}><Typography variant="caption" sx={{ color: '#64748b', display: 'block' }}>{chemoTranslations.table.customerName}</Typography><Typography variant="body2">{record[FIELD_NAMES.CDES]}</Typography></Grid>
                                <Grid item xs={4}>
                                    <Typography variant="caption" sx={{ color: '#64748b', display: 'block' }}>{chemoTranslations.table.willArrive}</Typography>
                                    {record[FIELD_NAMES.APPROVED] === SERVER_VALUE_YES ? <Chip size="small" label={CHECK_MARK} sx={{ backgroundColor: '#e8f5e9', color: '#388e3c', fontWeight: 700, height: 22 }} /> : <Typography sx={{ color: '#cbd5e0' }}>—</Typography>}
                                </Grid>
                                <Grid item xs={4}>
                                    <Typography variant="caption" sx={{ color: '#64748b', display: 'block' }}>{chemoTranslations.table.willNotArrive}</Typography>
                                    {record[FIELD_NAMES.NOT_APPROVED] === SERVER_VALUE_YES ? <Chip size="small" label={CHECK_MARK} sx={{ backgroundColor: '#ffebee', color: '#d32f2f', fontWeight: 700, height: 22 }} /> : <Typography sx={{ color: '#cbd5e0' }}>—</Typography>}
                                </Grid>
                                <Grid item xs={4}>
                                    <Typography variant="caption" sx={{ color: '#64748b', display: 'block' }}>{chemoTranslations.table.endTreatment}</Typography>
                                    {record[FIELD_NAMES.FINISH] === SERVER_VALUE_YES ? <Chip size="small" label={CHECK_MARK} sx={{ backgroundColor: '#e8eaf6', color: '#3949ab', fontWeight: 700, height: 22 }} /> : <Typography sx={{ color: '#cbd5e0' }}>—</Typography>}
                                </Grid>
                                <Grid item xs={6}><Typography variant="caption" sx={{ color: '#64748b', display: 'block' }}>{chemoTranslations.table.remainingDoses}</Typography><Typography variant="body2" sx={{ fontWeight: 700, color: '#5e72e4' }}>{record[FIELD_NAMES.ORDBAL]}</Typography></Grid>
                            </Grid>
                        </Paper>
                    ))}
                </Box>
            </AccordionDetails>
        </Accordion>
    )), [fetchedRecords, formatDate]);

    useEffect(() => {
        if (user?.hospital && !showForm) {
            fetchHospitalReport(user.hospital, topRecords);
        }
    }, [user?.hospital, topRecords, showForm, fetchHospitalReport]);

    useEffect(() => {
        if (searchParams.get("new") === "1" && canCreate) {
            openNewReportForm(user?.hospital);
            setSearchParams({}, { replace: true });
        }
    }, [searchParams, canCreate, openNewReportForm, setSearchParams]);

    return (
        <Box sx={styles.pageContainer}>
            {!showForm ? (
                <>
                    <ChemoRecordsHeader
                        searchTerm={searchTerm}
                        onSearchChange={handleSearchChange}
                        canCreate={canCreate}
                        onNewReport={() => openNewReportForm(user?.hospital)}
                        onOpenMessage={openMessageDialog}
                    />

                    {isLoadingRecords ? (
                        <Box sx={styles.loadingContainer}>
                            <CircularProgress />
                            <Typography variant="body1" sx={{ marginTop: 2 }}>
                                {chemoTranslations.form.loadingFromHospital}
                            </Typography>
                        </Box>
                    ) : fetchedRecords && fetchedRecords.length > 0 ? (
                        <Box sx={{ marginTop: 2 }}>
                            <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, justifyContent: 'space-between', alignItems: { xs: 'stretch', sm: 'center' }, marginBottom: 2, gap: { xs: 2, sm: 0 } }}>
                                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                                    {chemoTranslations.form.chemoRecordsPrefix}{user?.hospital || EMPTY_STRING}
                                </Typography>
                                <FormControl size="small" sx={{ minWidth: 120, width: { xs: '100%', sm: 'auto' } }}>
                                    <InputLabel id="top-records-label">{chemoTranslations.form.numRecordsLabel}</InputLabel>
                                    <Select labelId="top-records-label" value={topRecords || TOP_RECORDS_OPTIONS[1]} label={chemoTranslations.form.numRecordsLabel} onChange={(e) => handleTopRecordsChange(e.target.value, user?.hospital)}>
                                        {TOP_RECORDS_OPTIONS.map(option => <MenuItem key={option} value={option}>{option}</MenuItem>)}
                                    </Select>
                                </FormControl>
                            </Box>

                            <Paper sx={{ padding: { xs: 1.5, sm: 2, md: 2.5 }, marginBottom: 2, backgroundColor: '#f8fafb', border: '1px solid #e3f2fd', borderRadius: '10px', boxShadow: 'none' }}>
                                <Typography variant="subtitle1" sx={{ fontWeight: 600, marginBottom: 2, color: '#334155' }}>{chemoTranslations.form.dateRangeSearch}</Typography>
                                <LocalizationProvider dateAdapter={AdapterDayjs}>
                                <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 2, alignItems: { xs: 'stretch', sm: 'center' } }}>
                                    <DatePicker
                                        label={chemoTranslations.form.startDate}
                                        format="DD/MM/YYYY"
                                        value={dateSearch.startDate ? dayjs(dateSearch.startDate) : null}
                                        onChange={(val) => updateDateSearch('startDate', val ? val.format('YYYY-MM-DD') : '')}
                                        maxDate={dayjs()}
                                        slotProps={{ ...datePickerPopupSlotProps, textField: { size: 'small', required: true, sx: { ...styles.datePickerField, minWidth: { xs: '100%', sm: 200 } } } }}
                                    />
                                    <DatePicker
                                        label={chemoTranslations.form.endDate}
                                        format="DD/MM/YYYY"
                                        value={dateSearch.endDate ? dayjs(dateSearch.endDate) : null}
                                        onChange={(val) => updateDateSearch('endDate', val ? val.format('YYYY-MM-DD') : '')}
                                        maxDate={dayjs()}
                                        slotProps={{ ...datePickerPopupSlotProps, textField: { size: 'small', required: true, sx: { ...styles.datePickerField, minWidth: { xs: '100%', sm: 200 } } } }}
                                    />
                                    <Button variant="contained" startIcon={<SearchIcon />} onClick={handleDateSearch} disabled={isLoadingRecords} sx={{ ...styles.primaryButton, width: { xs: '100%', sm: 'auto' } }}>
                                        {chemoTranslations.form.search}
                                    </Button>
                                </Box>
                                </LocalizationProvider>
                            </Paper>

                            {accordionList}
                        </Box>
                    ) : loading ? (
                        <Box sx={styles.loadingContainer}><CircularProgress /></Box>
                    ) : (
                        <ChemoReportsList reports={filteredReports} canView={canView} onOpenReport={openExistingReport} />
                    )}
                </>
            ) : (
                <ChemoReportForm
                    report={currentReport}
                    onSubmit={handleSubmit}
                    onCancel={closeForm}
                    hospital={user?.hospital || EMPTY_STRING}
                    fetchedRecords={fetchedRecords}
                    isLoadingRecords={isLoadingRecords}
                    error={error}
                    handleCheckboxChange={handleCheckboxChange}
                    handleFieldChange={handleFieldChange}
                    formatDate={formatDate}
                    onSave={handleSaveReport}
                    onLoadPrevious={() => loadPreviousReport(user?.hospital)}
                />
            )}

            <CustomSnackbar open={snackbar.open} message={snackbar.message} severity={snackbar.severity} onClose={closeSnackbar} />

            {/* Result Dialog */}
            {(() => {
                const hasErrors = resultDialog.data?.errors?.length > 0;
                return (
                <Dialog open={resultDialog.open} onClose={closeResultDialog} maxWidth="sm" fullWidth dir="rtl"
                    PaperProps={{ sx: { borderRadius: '16px', overflow: 'hidden', boxShadow: '0 20px 60px rgba(0,0,0,0.15)' } }}>
                    <Box sx={{ background: hasErrors ? 'linear-gradient(135deg, #1976d2 0%, #1565c0 100%)' : 'linear-gradient(135deg, #43a047 0%, #2e7d32 100%)', px: 3, py: 2.5, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                            <Box sx={{ backgroundColor: 'rgba(255,255,255,0.2)', borderRadius: '50%', p: 0.75, display: 'flex' }}>
                                {hasErrors ? <InfoIcon sx={{ color: '#fff', fontSize: 22 }} /> : <SuccessIcon sx={{ color: '#fff', fontSize: 22 }} />}
                            </Box>
                            <Typography sx={{ color: '#fff', fontWeight: 700, fontSize: '1.05rem' }}>
                                {hasErrors ? chemoTranslations.dialog.saveWithErrors : chemoTranslations.dialog.savedSuccessfully}
                            </Typography>
                        </Box>
                        <Box sx={{ display: 'flex', gap: 1.5 }}>
                            <Chip icon={<SuccessIcon sx={{ color: '#fff !important', fontSize: '16px !important' }} />} label={`${resultDialog.data?.updatedCount || 0} ${chemoTranslations.dialog.recordsUpdatedSuccess}`} sx={{ backgroundColor: 'rgba(255,255,255,0.2)', color: '#fff', fontWeight: 600, fontSize: '0.8rem' }} />
                            {hasErrors && <Chip icon={<ErrorIcon sx={{ color: '#fff !important', fontSize: '16px !important' }} />} label={`${resultDialog.data.errors.length} ${chemoTranslations.dialog.recordsFailed}`} sx={{ backgroundColor: 'rgba(255,255,255,0.2)', color: '#fff', fontWeight: 600, fontSize: '0.8rem' }} />}
                        </Box>
                    </Box>
                    <DialogContent sx={{ px: 3, pt: 2.5, pb: 1 }}>
                        {resultDialog.data && (
                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                                {resultDialog.data.results?.length > 0 && (
                                    <Box>
                                        <Typography sx={{ fontWeight: 700, color: '#2e7d32', fontSize: '0.85rem', mb: 1 }}>{chemoTranslations.dialog.successfulRecords}</Typography>
                                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75, maxHeight: '160px', overflowY: 'auto' }}>
                                            {resultDialog.data.results.map((item, index) => (
                                                <Box key={index} sx={{ display: 'flex', alignItems: 'center', gap: 1.5, px: 2, py: 1, backgroundColor: '#f0faf1', borderRadius: '8px', border: '1px solid #c8e6c9' }}>
                                                    <Box sx={{ width: 7, height: 7, borderRadius: '50%', backgroundColor: '#43a047', flexShrink: 0 }} />
                                                    <Typography sx={{ fontSize: '0.88rem', fontWeight: 600, color: '#37474f' }}>{`${chemoTranslations.dialog.rowPrefix} ${item.rowId}: ${item.result?.[FIELD_NAMES.CDES] || ''}`}</Typography>
                                                </Box>
                                            ))}
                                        </Box>
                                    </Box>
                                )}
                                {hasErrors && (
                                    <Box>
                                        <Typography sx={{ fontWeight: 700, color: '#c62828', fontSize: '0.85rem', mb: 1 }}>{chemoTranslations.dialog.failedRecords}</Typography>
                                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75, maxHeight: '160px', overflowY: 'auto' }}>
                                            {resultDialog.data.errors.map((item, index) => (
                                                <Box key={index} sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5, px: 2, py: 1.25, backgroundColor: '#fff5f5', borderRadius: '8px', border: '1px solid #ffcdd2' }}>
                                                    <ErrorIcon sx={{ fontSize: 16, color: '#e53935', flexShrink: 0, mt: '2px' }} />
                                                    <Box>
                                                        <Typography sx={{ fontSize: '0.88rem', fontWeight: 700, color: '#37474f' }}>{`${chemoTranslations.dialog.rowPrefix} ${item.rowId}`}</Typography>
                                                        <Typography sx={{ fontSize: '0.8rem', color: '#b71c1c' }}>{item.error}</Typography>
                                                    </Box>
                                                </Box>
                                            ))}
                                        </Box>
                                    </Box>
                                )}
                            </Box>
                        )}
                    </DialogContent>
                    <DialogActions sx={{ px: 3, py: 2.5 }}>
                        <Button onClick={closeResultDialog} variant="contained" sx={{ borderRadius: '10px', px: 4, py: 1, fontWeight: 700, backgroundColor: hasErrors ? '#1976d2' : '#43a047', boxShadow: hasErrors ? '0 4px 14px rgba(25,118,210,0.35)' : '0 4px 14px rgba(67,160,71,0.35)', '&:hover': { backgroundColor: hasErrors ? '#1565c0' : '#2e7d32' }, textTransform: 'none', fontSize: '0.9rem' }}>{chemoTranslations.dialog.close}</Button>
                    </DialogActions>
                </Dialog>
                );
            })()}

            {/* Message Dialog */}
            <Dialog open={messageDialog.open} onClose={closeMessageDialog} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: { xs: '8px', md: '12px' } } }}>
                <DialogTitle sx={{ backgroundColor: '#2c3e50', color: '#fff', display: 'flex', alignItems: 'center', gap: 1.5 }}>
                    <MessageIcon />
                    <Typography variant="h6" sx={{ fontWeight: 600 }}>{chemoTranslations.message.titlePrefix}{user?.hospital || EMPTY_STRING}</Typography>
                </DialogTitle>
                <DialogContent sx={{ padding: { xs: 2, sm: 3 }, paddingTop: '24px !important' }}>
                    <Typography variant="body2" sx={{ color: '#546e7a', marginBottom: 2 }}>{chemoTranslations.message.writeMessage}</Typography>
                    <TextField fullWidth multiline minRows={4} maxRows={8} placeholder={chemoTranslations.message.placeholder} value={messageDialog.message} onChange={(e) => updateMessageText(e.target.value)} disabled={messageDialog.sending} inputProps={{ dir: 'rtl', style: { textAlign: 'right' } }} sx={{ '& .MuiOutlinedInput-root': { borderRadius: '8px' } }} />
                </DialogContent>
                <DialogActions sx={{ padding: '16px 24px', gap: 1 }}>
                    <Button onClick={closeMessageDialog} variant="outlined" disabled={messageDialog.sending} sx={styles.cancelButton}>{chemoTranslations.labels.cancel}</Button>
                    <Button onClick={() => handleSendMessage(user?.hospital)} variant="contained" disabled={!messageDialog.message?.trim() || messageDialog.sending} startIcon={messageDialog.sending ? <CircularProgress size={18} color="inherit" /> : <SendIcon />} sx={styles.primaryButton}>
                        {messageDialog.sending ? chemoTranslations.message.sending : chemoTranslations.message.send}
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default ChemoRecordsPage;
